import { useEffect, useState } from 'react';

/**
 * 深浅色。默认跟随系统，用户手动切过之后按用户的选择走。
 *
 * 与控制台其它部分一样：不引入第三方主题库，一个 class 加一段 localStorage 就够。
 */
const STORAGE_KEY = 'havorix.console.theme';

export type Theme = 'light' | 'dark' | 'system';

function readStored(): Theme {
  const value = window.localStorage.getItem(STORAGE_KEY);
  return value === 'light' || value === 'dark' || value === 'system' ? value : 'system';
}

function systemPrefersDark(): boolean {
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function apply(theme: Theme): void {
  const dark = theme === 'dark' || (theme === 'system' && systemPrefersDark());
  document.documentElement.classList.toggle('dark', dark);
}

export function useTheme() {
  const [theme, setTheme] = useState<Theme>(() => readStored());

  useEffect(() => {
    apply(theme);
    window.localStorage.setItem(STORAGE_KEY, theme);
  }, [theme]);

  useEffect(() => {
    // 跟随系统时，系统切换深浅色要立刻生效。
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    function onChange() {
      if (readStored() === 'system') apply('system');
    }
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  return {
    theme,
    /** 在浅色与深色之间直接切换（不经过 system），符合「点一下就要有变化」的直觉 */
    toggle: () => setTheme(systemPrefersDark() ? 'light' : 'dark'),
    isDark: theme === 'dark' || (theme === 'system' && systemPrefersDark()),
  };
}
